import { useEffect } from 'react'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import useDeadlineNotify from '../models/useDeadlineNotify'
import { leadTimeOptions } from '../utilities/deadlineNotice'

/**
 * The compute time limit email setting on the Account page (nobgp/app#354, router#1552).
 *
 * ⚠⚠ RENDERS NOTHING WHEN THE COLUMNS ARE NOT THERE. Before router migration 0199 is applied they
 * do not exist, and a switch there could only fail. The read is forced on each mount, so the block
 * appears on the next visit after the apply, with no reload.
 *
 * ⚠ A read that FAILED for any other reason is a different answer. The read runs once per mount
 * and never retries without `force`, so treating a network blip as "the setting does not exist"
 * deleted the setting from the page for the rest of the visit. It says so instead.
 *
 * The router sends the email. It goes to the person who provisioned the machine, or to the org
 * Owners who turned it on when that person left the org.
 */
export default function DeadlineEmailSettings() {
  const availability = useDeadlineNotify(s => s.availability)
  const enabled = useDeadlineNotify(s => s.enabled)
  const leadMinutes = useDeadlineNotify(s => s.leadMinutes)
  const saving = useDeadlineNotify(s => s.saving)
  const saveResult = useDeadlineNotify(s => s.saveResult)
  const save = useDeadlineNotify(s => s.save)

  useEffect(() => {
    void useDeadlineNotify.getState().get({ force: true })
    // A result from an earlier visit is not about this one.
    useDeadlineNotify.setState({ saveResult: null })
  }, [])

  if (availability === 'unavailable' || availability === 'unknown' || availability === 'loading') return null

  if (availability === 'error') {
    return (
      <>
        <Divider />
        <Box>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
            Compute time limit
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your email setting could not be read. Open this page again to try.
          </Typography>
        </Box>
      </>
    )
  }

  return (
    <>
      <Divider />
      <Box>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
          Compute time limit
        </Typography>
        <Stack spacing={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                disabled={saving}
                onChange={e => void save({ enabled: e.target.checked })}
              />
            }
            label="Email me before a machine reaches its time limit"
          />
          {/* ⚠ Not disabled while the email is off: the lead time also sets when the warning in the
              app starts (app#354 decision), and the switch controls only the email. */}
          <FormControl size="small" disabled={saving}>
            <InputLabel id="deadline-lead-label">When to warn me</InputLabel>
            <Select
              labelId="deadline-lead-label"
              label="When to warn me"
              value={leadMinutes}
              onChange={e => void save({ leadMinutes: Number(e.target.value) })}
            >
              {leadTimeOptions(leadMinutes).map(o => (
                <MenuItem key={o.minutes} value={o.minutes}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            This time applies to the email and to the warning in the app. When the email is on, you
            also get an email when the machine stops at its time limit.
          </Typography>
          {saveResult && (
            <Typography
              variant="caption"
              role="status"
              sx={{ color: saveResult === 'saved' ? 'success.main' : 'error.main' }}
            >
              {saveResult === 'saved' ? 'Saved.' : 'Not saved. Try again.'}
            </Typography>
          )}
        </Stack>
      </Box>
    </>
  )
}
